<template>
  <v-dialog v-model="showDialog" persistent max-width="580" class="profile-dialog-wrapper">
    <div v-if="showLoader" class="profile-loader-overlay">
      <div class="profile-spinner"></div>
    </div>
    
    <div class="profile-dialog">
      <div class="profile-header">
        <div class="profile-avatar-wrapper">
          <div class="profile-avatar">
            <img :src="user.profileImage" :alt="$t('profile-dialog.title')" class="profile-avatar-img" />
          </div>
          <button
            v-if="isEditing"
            class="profile-camera-btn"
            @click="openFileDialog"
            type="button"
          >
            <svg viewBox="0 0 24 24" class="profile-camera-icon">
              <path fill="currentColor" d="M4,4H7L9,2H15L17,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z" />
            </svg>
          </button>
        </div>
        
        <button
          class="profile-save-btn"
          :class="{ 'profile-save-btn-disabled': !isFormValid }"
          :disabled="!isFormValid"
          @click="saveProfile"
          type="button"
        >
          <svg viewBox="0 0 24 24" class="profile-btn-icon">
            <path fill="currentColor" d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z" />
          </svg>
          {{ $t('profile-dialog.save') }}
        </button>
      </div>
      
      <div class="profile-divider"></div>
      
      <div class="profile-content">
        <div class="profile-row">
          <div class="profile-col profile-col-half">
            <div class="profile-input-group">
              <label class="profile-label">
                <svg viewBox="0 0 24 24" class="profile-input-icon">
                  <path fill="currentColor" d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                </svg>
                {{ $t('profile-dialog.first-name') }}
              </label>
              <input
                v-model="user.firstName"
                :readonly="!isEditing"
                type="text"
                class="profile-input"
                :class="{ 'profile-input-error': errors.firstName }"
                @input="validateFirstName"
              />
              <span v-if="errors.firstName" class="profile-error-text">{{ errors.firstName }}</span>
            </div>
          </div>
          
          <div class="profile-col profile-col-half">
            <div class="profile-input-group">
              <label class="profile-label">
                <svg viewBox="0 0 24 24" class="profile-input-icon">
                  <path fill="currentColor" d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                </svg>
                {{ $t('profile-dialog.last-name') }}
              </label>
              <input
                v-model="user.lastName"
                :readonly="!isEditing"
                type="text"
                class="profile-input"
                :class="{ 'profile-input-error': errors.lastName }"
                @input="validateLastName"
              />
              <span v-if="errors.lastName" class="profile-error-text">{{ errors.lastName }}</span>
            </div>
          </div>
        </div>
        
        <div class="profile-row">
          <div class="profile-col profile-col-full">
            <div class="profile-input-group">
              <label class="profile-label">
                <svg viewBox="0 0 24 24" class="profile-input-icon">
                  <path fill="currentColor" d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
                </svg>
                {{ $t('profile-dialog.email') }}
              </label>
              <input
                v-model="user.email"
                :readonly="!isEditing"
                type="email"
                class="profile-input"
                :class="{ 'profile-input-error': errors.email }"
                @input="validateEmail"
              />
              <span v-if="errors.email" class="profile-error-text">{{ errors.email }}</span>
            </div>
          </div>
        </div>
        
        <div class="profile-row">
          <div class="profile-col profile-col-half">
            <div class="profile-input-group">
              <label class="profile-label">
                <svg viewBox="0 0 24 24" class="profile-input-icon">
                  <path fill="currentColor" d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z" />
                </svg>
                {{ $t('profile-dialog.campus') }}
              </label>
              <input
                :value="user.campus.name"
                type="text"
                class="profile-input profile-input-disabled"
                readonly
                disabled
              />
            </div>
          </div>
          
          <div class="profile-col profile-col-half">
            <div class="profile-input-group">
              <label class="profile-label">
                <svg viewBox="0 0 24 24" class="profile-input-icon">
                  <path fill="currentColor" d="M13,3V9H21V3M13,21H21V11H13M3,21H11V15H3M3,13H11V3H3V13Z" />
                </svg>
                {{ $t('profile-dialog.building') }}
              </label>
              <input
                :value="user.building.name"
                type="text"
                class="profile-input profile-input-disabled"
                readonly
                disabled
              />
            </div>
          </div>
        </div>
        
        <div class="profile-row">
          <div class="profile-col profile-col-full">
            <div class="profile-input-group">
              <label class="profile-label">
                <svg viewBox="0 0 24 24" class="profile-input-icon">
                  <path fill="currentColor" d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1Z" />
                </svg>
                {{ $t('profile-dialog.roles') }}
              </label>
              <input
                :value="user.roles"
                type="text"
                class="profile-input profile-input-disabled"
                readonly
                disabled
              />
            </div>
          </div>
        </div>
        
        <div class="profile-row">
          <div class="profile-col profile-col-full">
            <div class="profile-input-group">
              <label class="profile-label">
                <svg viewBox="0 0 24 24" class="profile-input-icon">
                  <path fill="currentColor" d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z" />
                </svg>
                {{ $t('profile-dialog.groups') }}
              </label>
              <input
                :value="user.groups"
                type="text"
                class="profile-input profile-input-disabled"
                readonly
                disabled
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <v-dialog v-model="showCropper" persistent max-width="500" class="profile-cropper-wrapper">
      <div class="profile-cropper-dialog">
        <div class="profile-cropper-header">
          <h3 class="profile-cropper-title">{{ $t('profile-dialog.crop-image') }}</h3>
        </div>
        
        <div class="profile-cropper-content">
          <cropper
            class="profile-cropper"
            :src="selectedImage"
            :stencil-props="{ aspectRatio: 1 }"
            @change="onCropComplete"
          />
        </div>
        
        <div class="profile-cropper-actions">
          <button class="profile-cropper-btn profile-cropper-btn-cancel" @click="cancelCrop" type="button">
            <svg viewBox="0 0 24 24" class="profile-btn-icon">
              <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
            {{ $t('profile-dialog.cancel') }}
          </button>
          <button class="profile-cropper-btn profile-cropper-btn-apply" @click="saveCroppedImage" type="button">
            <svg viewBox="0 0 24 24" class="profile-btn-icon">
              <path fill="currentColor" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
            </svg>
            {{ $t('profile-dialog.apply') }}
          </button>
        </div>
      </div>
    </v-dialog>
    
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      capture="environment"
      style="display: none"
      @change="onFileChange"
    />
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, defineExpose } from 'vue'
import { useRouter } from 'vue-router'
import { useOAuthService } from '@/services/OAuthService'
import lodash from 'lodash'
import { getStoredItem, setStoredItem, removeStoredItem } from '@/services/utils'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { useI18n } from 'vue-i18n'
import {
  getBuildings,
  getCampuses,
  getUserProfiles,
  getDataFromBlob,
  getFile,
  deleteProfileImage,
  postProfileImage,
  postFile,
  createUserProfile,
  getUserProfileById,
  updateUserProfile,
} from '@/controllers/BaseController'
import { getUsers, updateUser } from '@/controllers/BackboneAPI'
import { Capacitor } from '@capacitor/core'
import Swal from 'sweetalert2'

const router = useRouter()
const OAuthService = useOAuthService()
const { locale, t } = useI18n()

const showDialog = ref(false)
const showLoader = ref(false)
const isEditing = ref(true)
const showCropper = ref(false)
const fileInput = ref(null)
const selectedImage = ref(null)
const originalImage = ref(null)
const croppedImageBlob = ref(null)
const croppedImage = ref(null)
const apiProfiles = ref([])
const apiUsers = ref([])

const errors = ref({
  email: '',
  firstName: '',
  lastName: ''
})

const emailRules = [
  v => !!v || t('profile-dialog.email') + ' ' + t('profile-dialog.validation-error-message'),
  v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || t('profile-dialog.validation-error-message'),
  v => v.length <= 254 || t('profile-dialog.validation-error-message')
]

const firstNameRules = [
  v => !!v || t('profile-dialog.first-name') + ' ' + t('profile-dialog.validation-error-message'),
  v => v.length <= 50 || t('profile-dialog.validation-error-message'),
  v => /^[A-Za-z\s'-]+$/.test(v) || t('profile-dialog.validation-error-message')
]

const lastNameRules = [
  v => !!v || t('profile-dialog.last-name') + ' ' + t('profile-dialog.validation-error-message'),
  v => v.length <= 50 || t('profile-dialog.validation-error-message'),
  v => /^[A-Za-z\s'-]+$/.test(v) || t('profile-dialog.validation-error-message')
]

const isFormValid = computed(() => {
  return !errors.value.email && !errors.value.firstName && !errors.value.lastName
})

const validateEmail = () => {
  const value = user.value.email
  if (!value) {
    errors.value.email = t('profile-dialog.validation-error-message')
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    errors.value.email = t('profile-dialog.validation-error-message')
  } else if (value.length > 254) {
    errors.value.email = t('profile-dialog.validation-error-message')
  } else {
    errors.value.email = ''
  }
}

const validateFirstName = () => {
  const value = user.value.firstName
  if (!value) {
    errors.value.firstName = t('profile-dialog.validation-error-message')
  } else if (value.length > 50) {
    errors.value.firstName = t('profile-dialog.validation-error-message')
  } else if (!/^[A-Za-z\s'-]+$/.test(value)) {
    errors.value.firstName = t('profile-dialog.validation-error-message')
  } else {
    errors.value.firstName = ''
  }
}

const validateLastName = () => {
  const value = user.value.lastName
  if (!value) {
    errors.value.lastName = t('profile-dialog.validation-error-message')
  } else if (value.length > 50) {
    errors.value.lastName = t('profile-dialog.validation-error-message')
  } else if (!/^[A-Za-z\s'-]+$/.test(value)) {
    errors.value.lastName = t('profile-dialog.validation-error-message')
  } else {
    errors.value.lastName = ''
  }
}

const user = ref({
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  campus: { id: '', name: t('profile-dialog.campus') },
  building: { id: '', name: t('profile-dialog.building') },
  groups: [],
  roles: [],
  profileImage: '',
  files: [],
  hasUserProfile: false,
  hasProfileImage: null,
  userProfile: {},
})

const openFileDialog = async () => {
  const isNative = Capacitor.isNativePlatform()
  if (isNative) {
    try {
      const permission = await Camera.requestPermissions()
      if (permission?.camera !== 'granted' && permission?.photos !== 'granted') {
        Swal.fire({
          icon: 'warning',
          title: t('profile-dialog.camera-permission-title'),
          text: t('profile-dialog.camera-permission-message'),
        })
        return
      }
      const photo = await Camera.getPhoto({
        quality: 80,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
      })
      selectedImage.value = photo.dataUrl
      showCropper.value = true
    } catch (error) {
      console.error('Camera error:', error)
      Swal.fire({
        icon: 'error',
        title: t('profile-dialog.photo-access-error-title'),
        text: t('profile-dialog.photo-access-error-message'),
      })
    }
  } else {
    fileInput.value?.click()
  }
}

const onFileChange = (event) => {
  try {
    const file = event.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: t('profile-dialog.image-size-error-title'),
          text: t('profile-dialog.image-size-error-message'),
        })
        return
      }
      originalImage.value = user.value.profileImage
      selectedImage.value = URL.createObjectURL(file)
      showCropper.value = true
    }
  } catch (error) {
    console.error('Error selecting image:', error)
    Swal.fire({
      icon: 'error',
      title: t('profile-dialog.image-error-title'),
      text: t('profile-dialog.image-error-message'),
    })
  }
}

const onCropComplete = ({ canvas }) => {
  croppedImage.value = canvas.toDataURL()
  canvas.toBlob((blob) => {
    croppedImageBlob.value = blob
  }, 'image/png')
}

const saveCroppedImage = () => {
  user.value.profileImage = croppedImage.value
  showCropper.value = false
}

const cancelCrop = () => {
  user.value.profileImage = originalImage.value
  showCropper.value = false
  selectedImage.value = null
}

const saveProfile = async () => {
  validateEmail()
  validateFirstName()
  validateLastName()
  if (!isFormValid.value) {
    Swal.fire({
      icon: 'error',
      title: t('profile-dialog.validation-error-title'),
      text: t('profile-dialog.validation-error-message'),
    })
    return
  }
  try {
    Swal.fire({
      title: t('profile-dialog.saving'),
      text: t('profile-dialog.saving-message'),
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const profile = { ...user.value }
    const [apiProfiles, apiUsers] = await Promise.all([getUserProfiles(), getUsers()])
    let apiProfile = apiProfiles.find((user) => user.email === profile.email)
    let apiUser = apiUsers.find((user) => user.email === profile.email)
    const userPayload = {
      name: profile.name,
      email: profile.email,
      active: true,
      campusId: profile.campus.id,
      firstName: profile.firstName,
      lastName: profile.lastName,
    }
    if (apiUser) {
      apiUser = { ...apiUser, ...userPayload }
      await updateUser(apiUser.id, apiUser)
    }
    const profilePayload = {
      name: profile.name,
      displayName: profile.name,
      email: profile.email,
      displayEmail: profile.email,
      active: true,
      campusId: profile.campus.id,
    }
    let profileId
    if (apiProfile) {
      apiProfile = { ...apiProfile, ...profilePayload, version: apiProfile.version }
      await updateUserProfile(apiProfile.id, apiProfile)
      profileId = apiProfile.id
    } else {
      const newProfile = await createUserProfile(profilePayload)
      profileId = newProfile.id
    }
    if (croppedImageBlob.value) {
      if (user.value.hasProfileImage) {
        await deleteProfileImage(profileId, user.value.hasProfileImage)
      }
      const uploadedFile = await postFile(croppedImageBlob.value, 'image/png')
      const newFileId = uploadedFile.id
      await postProfileImage(profileId, newFileId)
      user.value.hasProfileImage = newFileId
    }
    await removeStoredItem('userProfiles')
    await removeStoredItem('users')
    if (!lodash.isNil(lodash.get(user.value, 'userProfile'))) {
      await removeStoredItem(`userProfile_${user.value.userProfile.id}`)
    }
    if (user.value.files?.length) {
      user.value.files.forEach((file) => {
        removeStoredItem(`fileData_${file.id}`)
      })
    }
    setStoredItem('ProfileCompleted', true)
    await Swal.fire({
      icon: 'success',
      title: t('profile-dialog.success-title'),
      text: t('profile-dialog.success-message'),
      showConfirmButton: false,
      timer: 1500,
      timerProgressBar: true,
    })
    window.location.reload()
  } catch (error) {
    console.error('Error saving profile:', error)
    Swal.fire({
      icon: 'error',
      title: t('profile-dialog.error-title'),
      text: t('profile-dialog.error-message'),
      confirmButtonText: 'OK',
    })
  } finally {
    showLoader.value = false
    Swal.close()
  }
}

onMounted(async () => {
  try {
    showLoader.value = true
    let userProfile = await OAuthService.getUserProfile()
    const [userProfiles, users] = await Promise.all([getUserProfiles(), getUsers()])
    apiProfiles.value = userProfiles
    apiUsers.value = users
    let apiProfile = userProfiles.find((user) => user.email === userProfile.email)
    if (apiProfile) {
      let profile = await getUserProfileById(apiProfile.id)
      apiProfile = profile
    }
    let [firstName, lastName] = lodash.split(
      userProfile.name || lodash.replace(userProfile.email.split('@')[0], /\./g, ' '),
      ' '
    )
    firstName = lodash.capitalize(firstName || '')
    lastName = lodash.capitalize(lastName || 'Unknown')
    let fallbackProfileImage = require('@/assets/General/user_avatar_3d.png')
    let profileImageId = lodash
      .get(apiProfile, 'files.entities', [])
      .find((file) => file.type === 'profileImage')?.id
    let image = profileImageId ? await getFile(profileImageId) : null
    let profileImage = image ? await getDataFromBlob(image) : fallbackProfileImage
    let hasProfileImage = image !== null ? profileImageId : null
    originalImage.value = profileImage
    let selectedBuilding =
      lodash.find(await getBuildings(), { name: await getStoredItem('selectedBuilding') }) || {
        name: t('profile-dialog.building'),
      }
    let selectedCampus =
      lodash.find(await getCampuses(), { id: lodash.get(selectedBuilding, 'campus.id') }) || {
        name: t('profile-dialog.campus'),
      }
    user.value = {
      name: `${firstName} ${lastName}`,
      email: lodash.get(userProfile, 'email', ''),
      firstName,
      lastName,
      profileImage,
      files: lodash.get(apiProfile, 'files.entities', []),
      hasUserProfile: apiProfile !== undefined,
      hasProfileImage,
      userProfile: apiProfile || {},
      groups:
        lodash.get(userProfile, 'groups', []).length
          ? userProfile.groups.join(' - ')
          : t('profile-dialog.groups'),
      roles:
        lodash
          .get(userProfile, 'realm_access.roles', [])
          .filter((role) => role !== 'uma_authorization')
          .join(' - ') || t('profile-dialog.roles'),
      building: selectedBuilding,
      campus: selectedCampus,
    }
    validateEmail()
    validateFirstName()
    validateLastName()
  } catch (error) {
    console.error('Error loading user profile:', error)
  } finally {
    showLoader.value = false
  }
})

const openDialog = () => {
  showDialog.value = true
}

defineExpose({
  openDialog,
})
</script>

<style scoped>
/* Vuetify Dialog Wrapper */
.profile-dialog-wrapper {
  z-index: 9999;
}

.profile-cropper-wrapper {
  z-index: 10000;
}

/* Loader Overlay */
.profile-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  border-radius: 24px;
}

.profile-spinner {
  width: 64px;
  height: 64px;
  border: 4px solid #e3f2fd;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: profileSpin 0.8s linear infinite;
}

@keyframes profileSpin {
  to {
    transform: rotate(360deg);
  }
}

/* Main Dialog */
.profile-dialog {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: profileSlideUp 0.4s ease-out;
}

@keyframes profileSlideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header */
.profile-header {
  background: linear-gradient(135deg, #bbdefb 0%, #e3f2fd 100%);
  padding: 32px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

.profile-avatar-wrapper {
  position: relative;
}

.profile-avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border: 4px solid #ffffff;
  background: #ffffff;
}

.profile-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-camera-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #1976d2;
  border: 3px solid #ffffff;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
  transition: all 0.3s ease;
}

.profile-camera-btn:hover {
  background: #1565c0;
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(25, 118, 210, 0.5);
}

.profile-camera-icon {
  width: 20px;
  height: 20px;
}

.profile-save-btn {
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  transition: all 0.3s ease;
}

.profile-save-btn:hover:not(.profile-save-btn-disabled) {
  background: linear-gradient(135deg, #43a047 0%, #66bb6a 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
}

.profile-save-btn-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.profile-btn-icon {
  width: 20px;
  height: 20px;
}

/* Divider */
.profile-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(0, 0, 0, 0.1), transparent);
}

/* Content */
.profile-content {
  padding: 28px;
  overflow-y: auto;
  flex: 1;
  max-height: calc(85vh - 200px);
}

.profile-content::-webkit-scrollbar {
  width: 8px;
}

.profile-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.profile-content::-webkit-scrollbar-thumb {
  background: #bbdefb;
  border-radius: 4px;
}

.profile-content::-webkit-scrollbar-thumb:hover {
  background: #90caf9;
}

/* Rows and Columns */
.profile-row {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.profile-row:last-child {
  margin-bottom: 0;
}

.profile-col {
  flex: 1;
  min-width: 0;
}

.profile-col-full {
  flex-basis: 100%;
}

.profile-col-half {
  flex-basis: calc(50% - 8px);
  min-width: 200px;
}

/* Input Groups */
.profile-input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.profile-label {
  font-size: 13px;
  font-weight: 600;
  color: #424242;
  display: flex;
  align-items: center;
  gap: 6px;
  letter-spacing: 0.3px;
}

.profile-input-icon {
  width: 18px;
  height: 18px;
  color: #1976d2;
}

.profile-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 14px;
  color: #212121;
  background: #ffffff;
  transition: all 0.3s ease;
  font-family: inherit;
}

.profile-input:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

.profile-input:read-only:not(.profile-input-disabled) {
  background: #f5f5f5;
  cursor: default;
}

.profile-input-disabled {
  background: #fafafa;
  color: #9e9e9e;
  cursor: not-allowed;
  border-color: #eeeeee;
}

.profile-input-error {
  border-color: #f44336;
}

.profile-input-error:focus {
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.1);
}

.profile-error-text {
  font-size: 12px;
  color: #f44336;
  margin-top: 4px;
  font-weight: 500;
}

/* Cropper Dialog */
.profile-cropper-dialog {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  width: 100%;
  overflow: hidden;
  animation: profileSlideUp 0.4s ease-out;
}

.profile-cropper-header {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  padding: 20px 24px;
}

.profile-cropper-title {
  font-size: 20px;
  font-weight: 700;
  color: #1565c0;
  margin: 0;
}

.profile-cropper-content {
  padding: 0;
  background: #000000;
}

.profile-cropper {
  height: 400px;
  background: #000000;
}

.profile-cropper-actions {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  padding: 20px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.profile-cropper-btn {
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.profile-cropper-btn-cancel {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  color: #c62828;
}

.profile-cropper-btn-cancel:hover {
  background: linear-gradient(135deg, #ffcdd2 0%, #ef9a9a 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(198, 40, 40, 0.2);
}

.profile-cropper-btn-apply {
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.profile-cropper-btn-apply:hover {
  background: linear-gradient(135deg, #43a047 0%, #66bb6a 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
}

/* Tablet Styles */
@media (max-width: 768px) {
  .profile-header {
    padding: 28px 24px;
  }

  .profile-content {
    padding: 24px;
    max-height: calc(80vh - 200px);
  }

  .profile-avatar {
    width: 80px;
    height: 80px;
  }

  .profile-camera-btn {
    width: 32px;
    height: 32px;
  }

  .profile-camera-icon {
    width: 18px;
    height: 18px;
  }

  .profile-col-half {
    flex-basis: 100%;
  }

  .profile-cropper {
    height: 350px;
  }
}

/* Mobile Styles */
@media (max-width: 480px) {
  .profile-header {
    padding: 24px 20px;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .profile-content {
    padding: 20px;
    max-height: calc(75vh - 200px);
  }

  .profile-avatar {
    width: 75px;
    height: 75px;
  }

  .profile-camera-btn {
    width: 30px;
    height: 30px;
  }

  .profile-camera-icon {
    width: 16px;
    height: 16px;
  }

  .profile-save-btn {
    padding: 10px 20px;
    font-size: 14px;
    width: 100%;
    justify-content: center;
  }

  .profile-row {
    gap: 12px;
    margin-bottom: 16px;
  }

  .profile-col-half {
    flex-basis: 100%;
  }

  .profile-input {
    padding: 11px 14px;
    font-size: 14px;
  }

  .profile-label {
    font-size: 12px;
  }

  .profile-cropper {
    height: 300px;
  }

  .profile-cropper-header {
    padding: 16px 20px;
  }

  .profile-cropper-title {
    font-size: 18px;
  }

  .profile-cropper-actions {
    padding: 16px 20px;
    flex-wrap: wrap;
  }

  .profile-cropper-btn {
    flex: 1;
    min-width: calc(50% - 6px);
    justify-content: center;
    padding: 10px 16px;
    font-size: 13px;
  }
}

/* Extra Small Mobile */
@media (max-width: 360px) {
  .profile-header {
    padding: 20px 16px;
  }

  .profile-content {
    padding: 16px;
  }

  .profile-avatar {
    width: 70px;
    height: 70px;
  }

  .profile-input {
    padding: 10px 12px;
    font-size: 13px;
  }

  .profile-cropper {
    height: 260px;
  }
}
</style>